import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

// Custom SVG Icons
const FeverIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 8C30.3431 8 29 9.34315 29 11V35C26.2386 36.3395 24.3333 39.1262 24.3333 42.3333C24.3333 47.0357 28.1977 50.8667 32.9 50.8667C37.6023 50.8667 41.4667 47.0357 41.4667 42.3333C41.4667 39.1262 39.5614 36.3395 36.8 35V11C36.8 9.34315 35.4569 8 33.8 8H32Z" fill="#79A8FF"/>
    <circle cx="32" cy="42" r="6" fill="#286CFF"/>
    <line x1="44" y1="16" x2="50" y2="16" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round"/>
    <line x1="44" y1="24" x2="48" y2="24" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round"/>
    <line x1="44" y1="32" x2="50" y2="32" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HeartIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 54C32 54 10 42 10 24C10 16 15 10 22 10C27 10 30 12 32 16C34 12 37 10 42 10C49 10 54 16 54 24C54 42 32 54 32 54Z" fill="#FF6B6B"/>
    <path d="M32 54C32 54 10 42 10 24C10 16 15 10 22 10C27 10 30 12 32 16" stroke="#FF4757" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M24 20L28 24L36 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChildCareIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="18" r="8" fill="#28C08A"/>
    <path d="M20 52V40C20 35.5817 23.5817 32 28 32H36C40.4183 32 44 35.5817 44 40V52" stroke="#28C08A" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="26" cy="16" r="2" fill="#1E8E6F"/>
    <circle cx="38" cy="16" r="2" fill="#1E8E6F"/>
    <path d="M28 20C28 20 30 22 32 22C34 22 36 20 36 20" stroke="#1E8E6F" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 30C16 30 12 26 12 22C12 18 14 16 16 16" stroke="#28C08A" strokeWidth="2" strokeLinecap="round"/>
    <path d="M48 30C48 30 52 26 52 22C52 18 50 16 48 16" stroke="#28C08A" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SkinIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="12" width="40" height="40" rx="8" fill="#FFD28A"/>
    <circle cx="24" cy="24" r="3" fill="#FFA94D"/>
    <circle cx="40" cy="28" r="4" fill="#FFA94D"/>
    <circle cx="28" cy="40" r="3.5" fill="#FFA94D"/>
    <path d="M34 24L38 28L36 32L32 30L34 24Z" fill="#FFA94D"/>
    <circle cx="44" cy="44" r="2.5" fill="#FFA94D"/>
  </svg>
);

const MentalWellnessIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 10C20 10 10 20 10 32C10 44 20 54 32 54C44 54 54 44 54 32C54 20 44 10 32 10Z" fill="#286CFF"/>
    <circle cx="24" cy="28" r="3" fill="white"/>
    <circle cx="40" cy="28" r="3" fill="white"/>
    <path d="M22 40C22 40 26 44 32 44C38 44 42 40 42 40" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <path d="M28 16C28 16 30 12 32 12C34 12 36 16 36 16" stroke="#5B8BFF" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="48" cy="20" r="2" fill="#5B8BFF"/>
    <circle cx="16" cy="20" r="2" fill="#5B8BFF"/>
  </svg>
);

const GeneralHealthIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" fill="#E8F5F1"/>
    <path d="M32 16V48M16 32H48" stroke="#28C08A" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="32" cy="32" r="20" stroke="#28C08A" strokeWidth="2"/>
    <path d="M32 12C32 12 28 8 24 8C20 8 16 12 16 16" stroke="#1E8E6F" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 12C32 12 36 8 40 8C44 8 48 12 48 16" stroke="#1E8E6F" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CommonConcerns = () => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const concerns = [
    {
      id: 1,
      icon: FeverIcon,
      title: 'Fever & Cold',
      description: 'Get instant consultation for fever, flu, and respiratory issues',
      color: '#79A8FF',
      darkColor: '#286CFF',
      gradient: 'linear-gradient(135deg, #E3EFFF 0%, #F0F6FF 100%)',
      borderGradient: 'linear-gradient(135deg, #79A8FF, #286CFF)'
    },
    {
      id: 2,
      icon: HeartIcon,
      title: 'Heart Health',
      description: 'Expert cardiology care for heart-related concerns',
      color: '#FF6B6B',
      darkColor: '#FF4757',
      gradient: 'linear-gradient(135deg, #FFE8E8 0%, #FFF0F0 100%)',
      borderGradient: 'linear-gradient(135deg, #FF6B6B, #FF4757)'
    },
    {
      id: 3,
      icon: ChildCareIcon,
      title: 'Child Care',
      description: 'Specialized pediatric consultation for your children',
      color: '#28C08A',
      darkColor: '#1E8E6F',
      gradient: 'linear-gradient(135deg, #E8F5F1 0%, #F0FAF6 100%)',
      borderGradient: 'linear-gradient(135deg, #28C08A, #1E8E6F)'
    },
    {
      id: 4,
      icon: SkinIcon,
      title: 'Skin Issues',
      description: 'Professional dermatology care for all skin conditions',
      color: '#FFD28A',
      darkColor: '#FFA94D',
      gradient: 'linear-gradient(135deg, #FFF4E3 0%, #FFF9F0 100%)',
      borderGradient: 'linear-gradient(135deg, #FFD28A, #FFA94D)'
    },
    {
      id: 5,
      icon: MentalWellnessIcon,
      title: 'Mental Wellness',
      description: 'Talk to therapists about stress, anxiety, and well-being',
      color: '#286CFF',
      darkColor: '#1E5ABF',
      gradient: 'linear-gradient(135deg, #E3EDFF 0%, #EEF4FF 100%)',
      borderGradient: 'linear-gradient(135deg, #286CFF, #1E5ABF)'
    },
    {
      id: 6,
      icon: GeneralHealthIcon,
      title: 'General Health',
      description: 'Comprehensive primary care from experienced doctors',
      color: '#28C08A',
      darkColor: '#1E8E6F',
      gradient: 'linear-gradient(135deg, #E8F5F1 0%, #F0FAF6 100%)',
      borderGradient: 'linear-gradient(135deg, #28C08A, #1E8E6F)'
    }
  ];

  // Check if mobile on mount and window resize
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scroll = (direction) => {
    if (isMobile) {
      // Mobile: Navigate slides
      if (direction === 'left' && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (direction === 'right' && currentSlide < concerns.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    } else {
      // Desktop: Scroll container
      const container = scrollContainerRef.current;
      if (container) {
        const scrollAmount = 340;
        const newScrollLeft = direction === 'left' 
          ? container.scrollLeft - scrollAmount 
          : container.scrollLeft + scrollAmount;
        
        container.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container && !isMobile) {
      setShowLeftArrow(container.scrollLeft > 10);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  // Update arrow visibility for mobile carousel
  React.useEffect(() => {
    if (isMobile) {
      setShowLeftArrow(currentSlide > 0);
      setShowRightArrow(currentSlide < concerns.length - 1);
    }
  }, [currentSlide, isMobile, concerns.length]);

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.badge}>
            <Sparkles size={16} style={{ color: '#FFB800' }} />
            <span>Premium Healthcare</span>
          </div>
          <h2 style={styles.title}>Consult Doctors for Common Health Concerns</h2>
          <p style={styles.subtitle}>
            Get expert medical advice for your health issues from verified doctors
          </p>
        </div>

        <div style={styles.scrollWrapper}>
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              style={{ ...styles.scrollButton, ...styles.leftButton }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #286CFF, #1E5ABF)';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <ChevronLeft size={24} style={{ color: '#286CFF' }} />
            </button>
          )}

          {/* Scrollable Container - Desktop / Mobile Carousel */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              ...styles.scrollContainer,
              ...(isMobile ? styles.mobileCarousel : {})
            }}
            className="scroll-container"
          >
            {concerns.map((concern, index) => {
              const IconComponent = concern.icon;
              const isVisible = !isMobile || index === currentSlide;
              
              return (
                <div
                  key={concern.id}
                  style={{
                    ...styles.card,
                    ...(isMobile ? styles.mobileCard : {}),
                    display: isVisible ? 'block' : 'none',
                    background: concern.gradient,
                    border: `3px solid transparent`,
                    backgroundImage: `${concern.gradient}, ${concern.borderGradient}`,
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box'
                  }}
                  className="hover-lift"
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                      e.currentTarget.style.boxShadow = `0 20px 60px ${concern.color}40, 0 0 0 4px ${concern.color}20`;
                      const icon = e.currentTarget.querySelector('.icon-wrapper');
                      if (icon) icon.style.transform = 'scale(1.1) rotate(5deg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = `0 8px 24px rgba(0, 0, 0, 0.1), 0 4px 12px ${concern.color}20`;
                      const icon = e.currentTarget.querySelector('.icon-wrapper');
                      if (icon) icon.style.transform = 'scale(1) rotate(0deg)';
                    }
                  }}
                >
                  <div style={styles.cardInner}>
                    <div className="icon-wrapper" style={styles.iconWrapper}>
                      <IconComponent />
                    </div>
                    <h3 style={styles.concernTitle}>{concern.title}</h3>
                    <p style={styles.concernDescription}>{concern.description}</p>
                    <button 
                      style={{
                        ...styles.consultBtn,
                        background: `linear-gradient(135deg, ${concern.color}, ${concern.darkColor})`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = `0 8px 24px ${concern.color}50`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = `0 4px 12px ${concern.color}30`;
                      }}
                    >
                      Consult Now →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Dot Indicators for Mobile */}
          {isMobile && (
            <div style={styles.dotIndicators}>
              {concerns.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  style={{
                    ...styles.dot,
                    ...(currentSlide === index ? styles.dotActive : {})
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              style={{ ...styles.scrollButton, ...styles.rightButton }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #286CFF, #1E5ABF)';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <ChevronRight size={24} style={{ color: '#286CFF' }} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '100px 0',
    background: 'linear-gradient(180deg, #FAFBFF 0%, #FFFFFF 100%)'
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 32px'
  },
  header: {
    textAlign: 'center',
    marginBottom: '64px'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #FFF9E6, #FFEDD5)',
    border: '2px solid #FFB800',
    borderRadius: '24px',
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#B8860B',
    marginBottom: '24px'
  },
  title: {
    fontSize: '48px',
    fontWeight: '800',
    lineHeight: '1.2',
    color: '#1A202C',
    marginBottom: '20px',
    letterSpacing: '-0.5px'
  },
  subtitle: {
    fontSize: '20px',
    color: '#64748B',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.7'
  },
  scrollWrapper: {
    position: 'relative',
    margin: '0 -32px',
    padding: '0 32px'
  },
  scrollContainer: {
    display: 'flex',
    gap: '24px',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollBehavior: 'smooth',
    padding: '16px 0 40px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch'
  },
  scrollButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'white',
    border: '3px solid #286CFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
    boxShadow: '0 8px 24px rgba(40, 108, 255, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  leftButton: {
    left: '16px'
  },
  rightButton: {
    right: '16px'
  },
  card: {
    borderRadius: '32px',
    padding: '4px',
    textAlign: 'center',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    minWidth: '340px',
    maxWidth: '340px',
    flexShrink: 0,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(40, 108, 255, 0.1)',
    position: 'relative'
  },
  cardInner: {
    background: 'white',
    borderRadius: '28px',
    padding: '48px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    backdropFilter: 'blur(10px)'
  },
  iconWrapper: {
    width: '96px',
    height: '96px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 28px',
    transform: 'scale(1) rotate(0deg)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))'
  },
  concernTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '16px',
    lineHeight: '1.3'
  },
  concernDescription: {
    fontSize: '16px',
    color: '#64748B',
    marginBottom: '32px',
    lineHeight: '1.7',
    minHeight: '56px',
    flex: 1
  },
  consultBtn: {
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    padding: '16px 36px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    width: '100%',
    boxShadow: '0 4px 12px rgba(40, 108, 255, 0.3)',
    letterSpacing: '0.3px'
  },
  // Mobile Carousel Styles
  mobileCarousel: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
    padding: '16px 0 60px'
  },
  mobileCard: {
    minWidth: '90%',
    maxWidth: '90%',
    margin: '0 auto'
  },
  dotIndicators: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '24px',
    padding: '0 0 20px'
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: '2px solid #286CFF',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: 0
  },
  dotActive: {
    background: '#286CFF',
    width: '32px',
    borderRadius: '6px'
  }
};

export default CommonConcerns;